import { useEffect, useMemo } from 'react';
import type { FormInstance } from 'antd/es/form';

import type { ILoginState } from '@share/store/slices';

import type { PaymentFormValues } from '../types';

import { INITIAL_VALUES } from '../constants';
import { minDownPaymentAmount, roundCurrency2 } from '../utils/payment-math';

interface UsePaymentFormInitialValuesParams {
  form: FormInstance<PaymentFormValues>;
  user: ILoginState['user'];
  certificateTemplateTotalPrice: number | undefined;
  holderOverride?: Partial<Pick<PaymentFormValues, 'firstName' | 'lastName' | 'email'>>;
  requiresInlineSignup?: boolean;
}

export function usePaymentFormInitialValues({
  form,
  user,
  certificateTemplateTotalPrice,
  holderOverride,
  requiresInlineSignup = false,
}: UsePaymentFormInitialValuesParams): { initialValues: PaymentFormValues } {
  const initialValues: PaymentFormValues = useMemo(
    () => ({
      ...INITIAL_VALUES,
      firstName: holderOverride?.firstName ?? (requiresInlineSignup ? '' : user?.firstName ?? ''),
      lastName: holderOverride?.lastName ?? (requiresInlineSignup ? '' : user?.lastName ?? ''),
      email: holderOverride?.email ?? (requiresInlineSignup ? '' : user?.email ?? ''),
      downPaymentAmount: roundCurrency2(
        minDownPaymentAmount(certificateTemplateTotalPrice ?? 0),
      ).toFixed(2),
    }),
    [user, certificateTemplateTotalPrice, holderOverride, requiresInlineSignup],
  );

  useEffect(() => {
    const down = minDownPaymentAmount(certificateTemplateTotalPrice ?? 0);
    form.setFieldValue('downPaymentAmount', roundCurrency2(down).toFixed(2));
  }, [certificateTemplateTotalPrice, form]);

  return { initialValues };
}
